import React, { useRef, useState } from 'react';

import { Footer, Header, Meta } from '$partials/global';
import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base';
import { ArtistListingItem, WorkListingItem } from '$partials/items';
import { useMultiSelectFilter } from '$hooks/filters';
import { LayoutContext, LayoutProvider } from '$partials/global/layout';
import FilterDrawer from '$partials/global/filter-drawer';
import { Button } from '$partials/ui';
import { IconClose, IconFilters } from '$partials/icon';
import TagCloud from '$partials/global/tag-cloud';
import Radio from '$partials/ui/radio';

const shuffle = (array) => {
    let shuffle = [...array];
    // Create a shallow copy to keep the original array immutable
    for (let i = shuffle.length - 1; i > 0; i--) {
    // Pick a random index from 0 to i
        const j = Math.floor(Math.random() * (i + 1));

        // Swap elements using destructuring assignment
        [shuffle[i], shuffle[j]] = [shuffle[j], shuffle[i]];
    }

    return shuffle;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').SearchPage}
 */
const SearchPage = (props) => {
    const bodyRef = useRef();
    const [sortOrder, setSortOrder] = useState(props.data?.sortOrder || 'relevance');
    const [seed, setSeed] = useState(new Date(Math.round(new Date().getTime() / (1000 * 60 * 2)) * (1000 * 60 * 2)).getTime());
    const [randomiseListing, setRandomiseListing] = useState(shuffle(props.data?.items?.edges,));

    const clientsFilter = useMultiSelectFilter({
        initialValue: props?.data?.items?.filters?.clients?.value,
        options: props?.data?.items?.filters?.clients?.options,
        facets: props?.items?.facets?.find((f) => f.key === props?.data?.items?.filters?.clients?.facet)?.buckets
    });
    const coloursFilter = useMultiSelectFilter({
        initialValue: props?.data?.items?.filters?.colours?.value,
        options: props?.data?.items?.filters?.colours?.options,
        facets: props?.items?.facets?.find((f) => f.key === props?.data?.items?.filters?.colours?.facet)?.buckets
    });
    const sectorsFilter = useMultiSelectFilter({
        initialValue: props?.data?.items?.filters?.sectors?.value,
        options: props?.data?.items?.filters?.sectors?.options,
        facets: props?.items?.facets?.find((f) => f.key === props?.data?.items?.filters?.sectors?.facet)?.buckets
    });
    const servicesFilter = useMultiSelectFilter({
        initialValue: props?.data?.items?.filters?.services?.value,
        options: props?.data?.items?.filters?.services?.options,
        facets: props?.items?.facets?.find((f) => f.key === props?.data?.items?.filters?.services?.facet)?.buckets
    });
    const subjectsFilter = useMultiSelectFilter({
        initialValue: props?.data?.items?.filters?.subjects?.value,
        options: props?.data?.items?.filters?.subjects?.options,
        facets: props?.items?.facets?.find((f) => f.key === props?.data?.items?.filters?.subjects?.facet)?.buckets
    });
    const filterOptions = [
        {
            key: 'sectorsFilter',
            label: 'Sector',
            ...sectorsFilter
        },
        {
            key: 'subjectsFilter',
            label: 'Subject',
            ...subjectsFilter
        },
        {
            key: 'servicesFilter',
            label: 'Service',
            ...servicesFilter
        },
        {
            key: 'clientsFilter',
            label: 'Client',
            ...clientsFilter
        },
        {
            key: 'coloursFilter',
            label: 'Colour',
            ...coloursFilter
        }
    ];

    const listing = useAjaxListing({
        infinite: sortOrder !== 'random' ,
        items: props.data?.items?.edges,
        path: '/api/v1/search',
        page: props.data?.items?.page || 1,
        totalItems: props.data?.items?.total || 0,
        totalPages: props.data?.items?.totalPages,
        query: {
            term: props.data.term,
            clients: clientsFilter.value,
            colours: coloursFilter.value,
            sectors: sectorsFilter.value,
            services: servicesFilter.value,
            subjects: subjectsFilter.value,
            sortOrder: sortOrder,
            ...(sortOrder === 'random' ? {seed: seed} : {} ),
        },
        onData: (data) => {
            setRandomiseListing([...randomiseListing, ...data.items]);
            clientsFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.clients?.facet)?.buckets);
            coloursFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.colours?.facet)?.buckets);
            sectorsFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.sectors?.facet)?.buckets);
            servicesFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.services?.facet)?.buckets);
            subjectsFilter.setFacets(data?.facets?.find((f) => f.key === page.items?.filters?.subjects?.facet)?.buckets);
        }
    });

    const clearFilters = () => {
        clientsFilter.setValue([]);
        coloursFilter.setValue([]);
        sectorsFilter.setValue([]);
        servicesFilter.setValue([]);
        subjectsFilter.setValue([]);
    };

    const splitIntoRows = (arr, numCols) => {
        return arr.reduce((cols, item, i) => {
            cols[i % numCols].push(item);
            return cols;
        }, Array.from({ length: numCols }, () => []));
    };

    return (
        <LayoutProvider>
            <LayoutContext.Consumer>
                {({ showSidebar, setShowSidebar }) => {
                    return (
                        <div className={`${showSidebar ? 'overflow-x-hidden' : ''}`}>
                            <div className={`w-full flex flex-col flex-shrink-0 min-h-dvh filter-dialog-page ${showSidebar ? 'filter-dialog-page__open' : ''}`}>
                                <Meta domain={props.domain} />
                                <Header
                                    {...props?.data?.header}
                                    bodyRef={bodyRef}
                                    domain={props.domain}/>
                                <FilterDrawer
                                    handleClose={() => setShowSidebar(false)}
                                    isNavOpen={showSidebar}>
                                    <div className='px-8 sm:px-16 flex flex-col pb-48'>
                                        <div className='py-8 sm:py-16 w-full flex justify-between'>
                                            <p className='type-style-m'>
                                                {'Filters'}
                                            </p>
                                            <Button
                                                as='div'
                                                className='underline underline-offset-2 type-style-s'
                                                onClick={clearFilters}>
                                                {'Clear all'}
                                            </Button>
                                        </div>
                                        <div className='py-8 sm:py-16 grid gap-y-20 sm:gap-y-28'>
                                            {/* Sort */}
                                            <div className='flex flex-col gap-y-8'>
                                                <p className='pb-12 sm:pb-16 type-style-s'>
                                                    {'Sort'}
                                                </p>
                                                <Radio
                                                    checked={sortOrder === 'relevance'}
                                                    label={'Relevance'}
                                                    name='sort'
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSortOrder('relevance');
                                                        }
                                                    }}
                                                    value={'relevance'} />
                                                <Radio
                                                    checked={sortOrder === 'random'}
                                                    label={'Random'}
                                                    name='sort'
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSortOrder('random');
                                                        }
                                                    }}
                                                    value={'random'} />
                                            </div>

                                            {/* Tag filters */}
                                            {filterOptions.map(cloud => {
                                                return (
                                                    <TagCloud
                                                        tagKey={cloud.key}
                                                        {...cloud} />
                                                );
                                            })}
                                            <div className='gap-4 flex-wrap flex'>
                                                {filterOptions.map(filter => {
                                                    if (filter.key === 'coloursFilter') { return null; }
                                                    return (
                                                        <>
                                                            {filter?.value?.map(value => {
                                                                const selected = filter.options.find(option => option.value === value);
                                                                if (!selected) {return null;}
                                                                return (
                                                                    <Button
                                                                        className='flex items-center button-filled'
                                                                        key={selected.id}
                                                                        onClick={() => {
                                                                            filter.toggleValue(selected.value);
                                                                        }}
                                                                        renderIcon={<IconClose/>}>
                                                                        {selected.label}
                                                                    </Button>
                                                                );
                                                            })}
                                                        </>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='fixed bottom-0 left-0 w-full bg-white p-8 sm:p-16 '>
                                        <Button
                                            className='flex items-center justify-center button-filled w-full mt-8'
                                            onClick={() => {
                                                const timestamp = new Date(Math.round(new Date().getTime() / (1000 * 60 * 2)) * (1000 * 60 * 2)).getTime();
                                                if (timestamp === seed) {
                                                    setRandomiseListing(shuffle(listing.items));
                                                }
                                                setSeed(timestamp);
                                                setShowSidebar(false);
                                            }}>
                                            {sortOrder === 'random' ? 'Show work' : `Show work (${listing.totalItems})`}
                                        </Button>
                                    </div>
                                </FilterDrawer>
                                <div className='min-h-[calc(100dvh-36px)] flex flex-col'>
                                    <main
                                        id='main-content'
                                        ref={bodyRef}>
                                        <div className='px-layout-outer pt-[100px] pb-32 sticky top-0 bg-white z-10'>
                                            <div className='-mx-layout-col flex'>
                                                <div className='px-layout-col flex flex-col w-full sm:w-3/7'>
                                                    <h1>
                                                        {'Results for: '}{props.data.term}
                                                    </h1>
                                                    {sortOrder !== 'random' &&  <p className='whitespace-pre-wrap sm:pr-[170px]'>
                                                        {listing.totalItems}{' items'}
                                                    </p>}
                                                    <Button
                                                        className='button-outline mt-16 flex items-center text-text-default-primary flex-row-reverse self-start'
                                                        onClick={() => setShowSidebar(true)}
                                                        renderIcon={<IconFilters className='mr-4 -ml-5 hover:text-text-default-invert'/>}>
                                                        {'Filters'}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        <AjaxListingContext.Provider value={listing}>
                                            <div className='bg-[white] z-10 relative flex flex-wrap'>
                                                <div className='sm:hidden w-full'>
                                                    {(sortOrder === 'random' ? randomiseListing : listing.items).map((item, idx) => {
                                                        const itm = item.node;
                                                        return (
                                                            <div key={itm.id}>
                                                                {itm.__typename === 'WorkDetailPage' ? <WorkListingItem {...itm}/> : <ArtistListingItem {...itm}/>}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <div className='hidden sm:flex w-full'>
                                                    <div className='w-2/7'>
                                                        {splitIntoRows(sortOrder === 'random' ? randomiseListing : listing.items, 3)[0].map((item, idx) => {
                                                            const itm = item.node;
                                                            return (
                                                                <div key={itm.id}>
                                                                    {itm.__typename === 'WorkDetailPage' ? (
                                                                        <WorkListingItem
                                                                            displayTitle={true}
                                                                            {...itm}/>
                                                                    ) : <ArtistListingItem {...itm}/> }
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <div className='w-3/7'>
                                                        {splitIntoRows(sortOrder === 'random' ? randomiseListing : listing.items, 3)[1].map((item, idx) => {
                                                            const itm = item.node;
                                                            return (
                                                                <div key={itm.id}>
                                                                    {itm.__typename === 'WorkDetailPage' ? (
                                                                        <WorkListingItem
                                                                            displayTitle={true}
                                                                            {...itm}/>
                                                                    ) : <ArtistListingItem {...itm}/>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <div className='w-2/7'>
                                                        {splitIntoRows(sortOrder === 'random' ? randomiseListing : listing.items, 3)[2].map((item, idx) => {
                                                            const itm = item.node;
                                                            return (
                                                                <div key={itm.id}>
                                                                    {itm.__typename === 'WorkDetailPage' ? (
                                                                        <WorkListingItem
                                                                            displayTitle={true}
                                                                            {...itm}/>
                                                                    ) : <ArtistListingItem {...itm}/>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* No result */}
                                                {listing.items?.length === 0 && (
                                                    <div className='px-layout-outer type-style-s'>
                                                        {'No results'}
                                                    </div>
                                                )}
                                            </div>
                                            <InfinitePagination />
                                        </AjaxListingContext.Provider>
                                    </main>

                                    <Footer {...props?.data?.footer} />
                                </div>
                            </div>
                        </div>
                    );
                }}
            </LayoutContext.Consumer>
        </LayoutProvider>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default SearchPage;
